import React from 'react';
import {List, ListItem, styled, TableCell} from '@mui/material';
import TextBreaks from '@components/common/TextBreaks';
import moment from '@lib/moment';
import {NILL} from '@lib/constants';
import { truncateString } from "@lib/utils";

/**
 * Helper to get a user-friendly name for a session
 */
export const getSessionName = ({ session, showNumbers = true, dateFallback = false, useNill = true }) => {
  if (!session) {
    return useNill ? NILL : '';
  }

  let name = session.lesson?.lesson_name || session.lesson?.booth_topic;

  // The 'session_name' field is used to add a custom label to the end of the lesson name. (Classes only)
  if (session.session_name && session.event.event_type === 'CLASS') {
    name = `${name} - ${session.session_name}`
  }
  if (!name && dateFallback && session.mStart) {
    name = session.mStart.format('MMM D, YYYY | h:mm A');
  }
  if (!name) {
    if (useNill) {
      name = NILL;
    } else {
      name = session.id;
    }
  }
  if (showNumbers) {
    if (session.sessionOrder) {
      name = `(${session.sessionOrder.order || '-'} / ${session.sessionOrder.totalNonCanceled || '-'}) ${name}`;
    } else {
      // the old way
      name = `(${session?.order || '-'} / ${session.event?.counts?.total || '-'}) ${name}`;
    }
  }
  return name;
};

/**
 * Helper to get the session order and number of sessions in the event (e.g., "Session 3 / 4")
 */
export const getSessionOrder = (session) => {
  const values = {
    totalNonCanceled: null,
    order: null,
    isFirstIncompleteSession: false,
  }

  if (session && session.event.sessions) {
    // Calculate session order
    const allSessions = session.event.sessions;
    const nonCanceled = orderSessions(allSessions.filter((s) => s.status !== 'CANCELED'));
    values.totalNonCanceled = nonCanceled.length;

    if (session.status !== 'CANCELED') {
      values.order = nonCanceled.findIndex((os) => os.id === session.id) + 1;
    }

    // Is this the first incomplete session?
    if (session.status === 'INCOMPLETE' || !session.status) {
      const incomplete = orderSessions(allSessions.filter((s) => s.status === 'INCOMPLETE' || !s.status));
      values.isFirstIncompleteSession = incomplete.findIndex((s) => s.id === session.id) === 0;
    }
  }
  return values;
};

export const orderSessions = (sessions) => {
  sessions.sort((a, b) => {
    const aStart = moment(a.start);
    const bStart = moment(b.start);
    return aStart - bStart;
  });
  return sessions;
};

export const formatSessionDates = (start, end) => {
  const mStart = moment(start);
  const mEnd = moment(end);
  const diff = mEnd.diff(mStart, 'minutes');
  if (diff > ((24*60) - 1)) {
    return `${mStart.format('MM/DD/YYYY h:mm A')} - ${mEnd.format('MM/DD/YYYY h:mm A')}`;
  }
  return `${mStart.format('MM/DD/YYYY h:mm A')} - ${mEnd.format('h:mm A')}`;
};

export const getDeliveryInfo = (session) => {
  // Sort delivery methods so In Person comes first and attach associated field data
  const methods = session.delivery_method?.sort() || [];
  let details;
  if (methods && methods.length) {
    details = methods.map((method) => {
      switch (method) {
        case 'In Person':
          return [session.delivery_location];
        case 'Virtual/Live Online':
          return [truncateString(session.delivery_url, 50), session.other_details];
        default:
          return [];
      }
    });
  } else {
    details = [session?.delivery_location, truncateString(session?.delivery_url, 50), session?.other_details].filter((item) => !!item);
  }
  return { methods, details };
};

export const getDeliveryCells = (session, siteOnly = false) => {
  const { methods, details } = getDeliveryInfo(session);

  const CellList = styled(List)({
    listStyleType: 'disc',
    padding: 0,
    paddingLeft: '18px',
    '& .MuiListItem-root': {
      display: 'list-item',
      paddingTop: 0,
      paddingBottom: 0,
    },
  });
  return (
    <>
      {!siteOnly && (
        <TableCell align="left">
          { methods.length > 1 ? (
            <CellList>
              { methods.map((method, i) => (
                <ListItem key={`method-${i}`} disableGutters>{ method }</ListItem>
              ))}
            </CellList>
          ) : <TextBreaks texts={methods} /> }
        </TableCell>
      )}
      <TableCell align="left">
        { details.length > 1 ? (
          <CellList>
            { details.map((detail, i) => (
              <ListItem key={`method-details-${i}`} disableGutters>
                <TextBreaks texts={ detail } />
              </ListItem>
            ))}
          </CellList>
        ) : <TextBreaks texts={details} /> }
      </TableCell>
    </>
  );
};

export const isYouth = (session) => {
  if (!session) {
    return false;
  }
  if (session.event.event_type === 'BOOTH') {
    return false;
  }
  if (session.event.event_type === 'CLASS') {
    return session.lesson?.audience === 'Youth';
  }
  return false;
}

/**
 * On the session form, for booth events, the booth topics are different depending on the program area of the event.
 * This function determines which booth topics are valid for a given event, based on the event's program area.
 * @param {string} boothTopic
 * @param event
 * @returns {boolean}
 */
export const boothTopicMatchesEvent = (boothTopic, event) => {
  for (const pa of event.program_areas) {
    if (boothTopic[pa]) {
      return true;
    }
  }
  return false;
}
